// src/features/delegations/pages/RevisionDiffPage.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { delegationsAPI } from '../services/delegations';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import PageHeader from '../../../components/common/PageHeader';
import { useBreadcrumbLabel } from '../../../context/BreadcrumbContext';

// ── Word-level LCS diff ───────────────────────────────────────────────────────
// Tokenises text into words and whitespace runs, then computes a Myers-style
// LCS and backtracks to produce { type: 'unchanged'|'removed'|'added', text }
// chunks suitable for inline rendering.

function tokenize(text) {
  // Split on whitespace boundaries, keeping the whitespace tokens so that
  // the reconstructed text is identical to the original.
  return (text ?? '').split(/(\s+)/);
}

function buildLCS(a, b) {
  const m = a.length;
  const n = b.length;
  // Use a flat typed array for performance on longer texts.
  const dp = new Int32Array((m + 1) * (n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i * (n + 1) + j] =
        a[i - 1] === b[j - 1]
          ? dp[(i - 1) * (n + 1) + (j - 1)] + 1
          : Math.max(dp[(i - 1) * (n + 1) + j], dp[i * (n + 1) + (j - 1)]);
    }
  }
  return dp;
}

function wordDiff(oldText, newText) {
  const a = tokenize(oldText);
  const b = tokenize(newText);
  const dp = buildLCS(a, b);
  const n = b.length;

  const chunks = [];
  let i = a.length;
  let j = b.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      chunks.unshift({ type: 'unchanged', text: a[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i * (n + 1) + (j - 1)] >= dp[(i - 1) * (n + 1) + j])) {
      chunks.unshift({ type: 'added', text: b[j - 1] });
      j--;
    } else {
      chunks.unshift({ type: 'removed', text: a[i - 1] });
      i--;
    }
  }

  return chunks;
}

// ── Rendering helpers ─────────────────────────────────────────────────────────

function DiffText({ chunks }) {
  const hasChanges = chunks.some((c) => c.type !== 'unchanged');
  if (!hasChanges) {
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {chunks.map((c) => c.text).join('')}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>
      {chunks.map((chunk, idx) => {
        if (chunk.type === 'unchanged') {
          return <span key={idx}>{chunk.text}</span>;
        }
        if (chunk.type === 'removed') {
          return (
            <span
              key={idx}
              style={{
                backgroundColor: 'rgba(244, 67, 54, 0.15)',
                textDecoration: 'line-through',
                color: '#c62828',
              }}
            >
              {chunk.text}
            </span>
          );
        }
        // added
        return (
          <span
            key={idx}
            style={{
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
              color: '#2e7d32',
            }}
          >
            {chunk.text}
          </span>
        );
      })}
    </Typography>
  );
}

function DateDiff({ oldValue, newValue, label }) {
  const fmt = (v) => (v ? format(new Date(v), 'dd MMM yyyy') : '—');
  const changed = oldValue !== newValue;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight="medium">
        {label}
      </Typography>
      {changed ? (
        <Box display="flex" alignItems="center" gap={1} mt={0.25} flexWrap="wrap">
          <Typography
            variant="body2"
            sx={{ textDecoration: 'line-through', color: 'error.main' }}
          >
            {fmt(oldValue)}
          </Typography>
          <Typography variant="body2" color="text.secondary">→</Typography>
          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
            {fmt(newValue)}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" mt={0.25}>{fmt(newValue)}</Typography>
      )}
    </Box>
  );
}

function FieldDiff({ label, oldText, newText }) {
  const { t } = useTranslation('delegations');
  const chunks = wordDiff(oldText, newText);
  const changed = chunks.some((c) => c.type !== 'unchanged');

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="caption" color="text.secondary" fontWeight="medium">
          {label}
        </Typography>
        {!changed && (
          <Chip label={t('delegations:diff.unchanged')} size="small" variant="outlined" color='primary' sx={{ fontSize: 10, height: 18 }} />
        )}
      </Box>
      <DiffText chunks={chunks} />
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RevisionDiffPage() {
  const { t } = useTranslation(['delegations', 'common']);
  const { delegationId, revisionId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();

  const [state, setState] = useState({
    loading: true,
    error: null,
    newRev: null,
    prevRev: null,
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // 1. Fetch history to find the previous revision
        const histRes = await delegationsAPI.getRevisionHistory(delegationId);
        if (cancelled) return;

        const history = histRes.data?.data ?? [];
        const sorted = [...history].sort((a, b) => a.revision_number - b.revision_number);
        const idx = sorted.findIndex((r) => r.revision_id === revisionId);

        if (idx === -1) {
          setState({ loading: false, error: t('delegations:diff.error_not_found'), newRev: null, prevRev: null });
          return;
        }

        const prevSummary = idx > 0 ? sorted[idx - 1] : null;

        // 2. Fetch full details for both in parallel
        const [newRevRes, prevRevRes] = await Promise.all([
          delegationsAPI.getRevision(delegationId, revisionId),
          prevSummary ? delegationsAPI.getRevision(delegationId, prevSummary.revision_id) : Promise.resolve(null),
        ]);
        if (cancelled) return;

        setState({
          loading: false,
          error: null,
          newRev: newRevRes?.data?.data ?? null,
          prevRev: prevRevRes?.data?.data ?? null,
        });
      } catch (e) {
        if (!cancelled) {
          setState({
            loading: false,
            error: e.response?.data?.message || e.message || t('delegations:diff.error_generic'),
            newRev: null,
            prevRev: null,
          });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [delegationId, revisionId, t]);

  const { loading, error, newRev, prevRev } = state;
  // Parent crumb is the delegation itself, named after the revision's title
  useBreadcrumbLabel(delegationId, newRev?.title);

  if (loading) {
    return (
      <Box maxWidth={860} mx="auto">
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={500} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth={860} mx="auto">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const isFirstRevision = !prevRev;

  const CONTENT_FIELDS = [
    { key: 'title', label: t('delegations:create.fields.title') },
    { key: 'task_description', label: t('delegations:create.fields.task_description') },
    { key: 'steps', label: t('delegations:create.fields.steps') },
    { key: 'competency_requirements', label: t('delegations:create.fields.competency_requirements') },
    { key: 'contraindications', label: t('delegations:create.fields.contraindications') },
    { key: 'supervision', label: t('delegations:create.fields.supervision') },
  ];

  return (
    <Box>
      {/* Header */}
      <PageHeader
        onBack={() => navigateBack(-1)}
        title={isFirstRevision ? t('delegations:diff.title_first', { n: newRev.revision_number }) : t('delegations:diff.title_compare', { from: prevRev.revision_number, to: newRev.revision_number })}
        subtitle={newRev.title}
        crumb={t('delegations:detail.revision_number', { n: newRev.revision_number })}
      />

      {isFirstRevision && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('delegations:diff.first_revision_info')}
        </Alert>
      )}

      <Paper variant="outlined">
        {/* Content fields */}
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t('delegations:detail.section.content')}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {CONTENT_FIELDS.map((field, i) => (
              <React.Fragment key={field.key}>
                {i > 0 && <Divider />}
                <FieldDiff
                  label={field.label}
                  oldText={prevRev?.[field.key] ?? ''}
                  newText={newRev[field.key] ?? ''}
                />
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <Divider />

        {/* Dates */}
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t('delegations:detail.section.validity')}
          </Typography>
          <Box display="flex" gap={4} flexWrap="wrap">
            <DateDiff
              label={t('delegations:create.fields.issued_date')}
              oldValue={prevRev?.issued_date ?? null}
              newValue={newRev.issued_date}
            />
            <DateDiff
              label={t('delegations:create.fields.review_date')}
              oldValue={prevRev?.review_date ?? null}
              newValue={newRev.review_date}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
